import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Send, Inbox, FileEdit, Plus, Trash2, Star, Search, ArrowLeft, Check, CheckCheck, BookUser, Pencil, X, ChevronRight, ChevronDown, Reply, Forward, Printer, Building2 } from 'lucide-react';
import { usePermissions } from '../../../contexts/PermissionsContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../components/Toast';
import Modal from '../../../components/Modal';
import ConfirmDialog from '../../../components/ConfirmDialog';
import api from '../../../services/api';

// ==================== COMPOSE ====================

function ComposeForm({ initial, onSend, onSaveDraft, onCancel, replyTo, forwardMsg }) {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    subject: initial?.subject || (replyTo ? `Re: ${replyTo.subject}` : '') || (forwardMsg ? `Fwd: ${forwardMsg.subject}` : ''),
    body: initial?.body || (forwardMsg ? `\n\n--- Przekazana wiadomość ---\nOd: ${forwardMsg.sender_name}\nData: ${new Date(forwardMsg.sent_at || forwardMsg.created_at).toLocaleString('pl-PL')}\n\n${forwardMsg.body}` : '') || (replyTo ? `\n\n--- ${replyTo.sender_name}, ${new Date(replyTo.sent_at || replyTo.created_at).toLocaleString('pl-PL')} ---\n${replyTo.body}` : ''),
    priority: 'normal',
    recipients: initial?.recipients?.map(r => r.recipient_id || r.id) || (replyTo && replyTo.sender_id != null ? [replyTo.sender_id] : []),
  });
  const [search, setSearch] = useState('');

  useEffect(() => {
    api.get('/messages/users/list').then(r => {
      setUsers(r.data.users);
      // Ensure replyTo recipient is set after users load
      if (replyTo?.sender_id && form.recipients.length === 0) {
        setForm(f => ({ ...f, recipients: [replyTo.sender_id] }));
      }
    }).catch(() => {});
  }, []); // eslint-disable-line

  const toggleRecipient = (id) => {
    setForm(f => ({ ...f, recipients: f.recipients.includes(id) ? f.recipients.filter(r => r !== id) : [...f.recipients, id] }));
  };

  const filteredUsers = users.filter(u => u.name.toLowerCase().includes(search.toLowerCase()) || u.email.toLowerCase().includes(search.toLowerCase()));
  const selectedUsers = users.filter(u => form.recipients.includes(u.id));

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('messages.toLabel')}</label>
        {selectedUsers.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {selectedUsers.map(u => (
              <span key={u.id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">
                {u.name}<button onClick={() => toggleRecipient(u.id)} className="hover:text-blue-900"><X size={12} /></button>
              </span>
            ))}
          </div>
        )}
        <div className="relative">
          <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm" placeholder={t('messages.searchUser')} />
        </div>
        {search && (
          <div className="border border-gray-200 rounded-lg mt-1 max-h-32 overflow-auto">
            {filteredUsers.map(u => (
              <button key={u.id} onClick={() => { toggleRecipient(u.id); setSearch(''); }}
                className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 flex items-center justify-between ${form.recipients.includes(u.id) ? 'bg-blue-50' : ''}`}>
                <span>{u.name} <span className="text-gray-400 text-xs">{u.email}</span>{u.tenant_name && <span className="text-blue-400 text-xs ml-1">({u.tenant_name})</span>}</span>
                {form.recipients.includes(u.id) && <Check size={14} className="text-blue-600" />}
              </button>
            ))}
            {filteredUsers.length === 0 && <div className="px-3 py-2 text-xs text-gray-400">{t('common.noResults')}</div>}
          </div>
        )}
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('messages.subjectLabel')}</label>
        <input type="text" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" required />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('messages.bodyLabel')}</label>
        <textarea value={form.body} onChange={e => setForm({ ...form, body: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" rows={8} required />
      </div>
      <div className="flex items-center gap-2 pt-2">
        <button onClick={() => onSend({ ...form })} disabled={!form.subject || !form.body || form.recipients.length === 0}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-lg text-sm">
          <Send size={16} /> {t('messages.sendButton')}
        </button>
        <button onClick={() => onSaveDraft({ ...form })} disabled={!form.subject}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-600 hover:bg-gray-50 rounded-lg text-sm">
          <FileEdit size={16} /> {t('messages.saveDraft')}
        </button>
        <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-500 hover:bg-gray-50 rounded-lg">{t('common.cancel')}</button>
      </div>
    </div>
  );
}

// ==================== TREE ====================

function buildTree(messages, type) {
  // Group by category (internal/collaboration/client), then by tenant
  const groups = { internal: {}, collaboration: {}, client: {} };
  messages.forEach(m => {
    const cat = type === 'inbox'
      ? (m.message_category || 'client')
      : 'collaboration';
    const tenantName = type === 'inbox'
      ? (m.sender_tenant_name || 'System')
      : (m.recipients?.[0]?.tenant_name || 'System');
    const tenantId = type === 'inbox'
      ? (m.sender_tenant_id || 0)
      : (m.recipients?.[0]?.tenant_id || 0);

    if (!groups[cat]) groups[cat] = {};
    const key = `${tenantId}_${tenantName}`;
    if (!groups[cat][key]) groups[cat][key] = { tenantName, tenantId, messages: [], unread: 0 };
    groups[cat][key].messages.push(m);
    if (type === 'inbox' && !m.read_at) groups[cat][key].unread++;
  });

  // For outbox, categorize by recipient tenant
  if (type === 'outbox') {
    const outGroups = { internal: {}, collaboration: {}, client: {} };
    messages.forEach(m => {
      (m.recipients || []).forEach(r => {
        const cat = r.tenant_id === messages._userTenantId ? 'internal' : 'collaboration';
        const key = `${r.tenant_id}_${r.tenant_name}`;
        if (!outGroups[cat]) outGroups[cat] = {};
        if (!outGroups[cat][key]) outGroups[cat][key] = { tenantName: r.tenant_name || 'System', tenantId: r.tenant_id, messages: [], unread: 0 };
        if (!outGroups[cat][key].messages.find(om => om.id === m.id)) {
          outGroups[cat][key].messages.push(m);
        }
      });
    });
    return outGroups;
  }

  return groups;
}

function MessageTree({ inbox, outbox, drafts, trash, activeFilter, onFilterChange, userTenantId }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState({ inbox: true, outbox: false });
  const [expandedCats, setExpandedCats] = useState({});

  const toggleExp = (key) => setExpanded(e => ({ ...e, [key]: !e[key] }));
  const toggleCat = (key) => setExpandedCats(e => ({ ...e, [key]: !e[key] }));

  const inboxTree = buildTree(inbox, 'inbox');
  // Tag outbox messages with user tenant ID for categorization
  outbox._userTenantId = userTenantId;
  const outboxTree = buildTree(outbox, 'outbox');

  const catLabels = { internal: t('messages.internal') || 'Wewnętrzne', collaboration: t('messages.collaboration') || 'Współpraca', client: t('messages.clients') || 'Klienci' };

  const renderCategory = (tree, catKey, parentKey) => {
    const tenants = tree[catKey] || {};
    const tenantList = Object.values(tenants);
    if (tenantList.length === 0) return null;
    const totalUnread = tenantList.reduce((s, t) => s + t.unread, 0);
    const ck = `${parentKey}_${catKey}`;
    const isOpen = expandedCats[ck] !== false;

    return (
      <div key={catKey}>
        <button onClick={() => toggleCat(ck)} className="w-full flex items-center gap-1.5 px-2 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded">
          {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <span className="font-medium">{catLabels[catKey]}</span>
          {totalUnread > 0 && <span className="px-1.5 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full ml-auto">{totalUnread}</span>}
        </button>
        {isOpen && tenantList.map(tn => {
          const filterKey = `${parentKey}_${catKey}_${tn.tenantId}`;
          const isActive = activeFilter === filterKey;
          return (
            <div key={filterKey} className="ml-3">
              <button onClick={() => onFilterChange(filterKey, parentKey, tn.tenantId, tn.messages)}
                className={`w-full flex items-center gap-1.5 px-2 py-1.5 text-sm rounded transition ${isActive ? 'bg-blue-100 text-blue-700 font-medium' : 'text-gray-500 hover:bg-gray-50'}`}>
                <Building2 size={13} className={isActive ? 'text-blue-500' : 'text-gray-400'} />
                <span className="truncate">{tn.tenantName}</span>
                <span className="text-xs text-gray-400 ml-auto">{tn.messages.length}</span>
                {tn.unread > 0 && <span className="px-1 py-0.5 bg-blue-500 text-white text-[9px] font-bold rounded-full">{tn.unread}</span>}
              </button>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="text-sm">
      {/* Inbox */}
      <button onClick={() => { toggleExp('inbox'); onFilterChange('inbox_all', 'inbox', null, inbox); }}
        className={`w-full flex items-center gap-2 px-2 py-2 font-bold rounded ${activeFilter === 'inbox_all' ? 'bg-blue-50 text-blue-700' : 'text-gray-800 hover:bg-gray-100'}`}>
        {expanded.inbox ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <Inbox size={16} className="text-blue-600" />
        {t('messages.inboxTab')}
        {inbox.filter(m => !m.read_at).length > 0 && <span className="px-1.5 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full ml-auto">{inbox.filter(m => !m.read_at).length}</span>}
      </button>
      {expanded.inbox && (
        <div className="ml-2">
          {renderCategory(inboxTree, 'internal', 'inbox')}
          {renderCategory(inboxTree, 'collaboration', 'inbox')}
          {renderCategory(inboxTree, 'client', 'inbox')}
        </div>
      )}

      {/* Outbox */}
      <button onClick={() => { toggleExp('outbox'); onFilterChange('outbox_all', 'outbox', null, outbox); }}
        className={`w-full flex items-center gap-2 px-2 py-2 font-bold rounded mt-1 ${activeFilter === 'outbox_all' ? 'bg-green-50 text-green-700' : 'text-gray-800 hover:bg-gray-100'}`}>
        {expanded.outbox ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <Send size={16} className="text-green-600" />
        {t('messages.outboxTab')}
      </button>
      {expanded.outbox && (
        <div className="ml-2">
          {renderCategory(outboxTree, 'internal', 'outbox')}
          {renderCategory(outboxTree, 'collaboration', 'outbox')}
          {renderCategory(outboxTree, 'client', 'outbox')}
        </div>
      )}

      {/* Drafts */}
      <button onClick={() => onFilterChange('drafts', 'drafts', null, drafts)}
        className={`w-full flex items-center gap-2 px-2 py-2 font-bold rounded mt-1 ${activeFilter === 'drafts' ? 'bg-amber-50 text-amber-700' : 'text-gray-800 hover:bg-gray-100'}`}>
        <FileEdit size={16} className="text-amber-500" />
        {t('messages.draftsFolder')}
        {drafts.length > 0 && <span className="text-xs text-gray-400 ml-auto">{drafts.length}</span>}
      </button>

      {/* Trash */}
      <button onClick={() => onFilterChange('trash', 'trash', null, trash)}
        className={`w-full flex items-center gap-2 px-2 py-2 font-bold rounded mt-1 ${activeFilter === 'trash' ? 'bg-red-50 text-red-700' : 'text-gray-800 hover:bg-gray-100'}`}>
        <Trash2 size={16} className="text-red-400" />
        {t('messages.trashFolder')}
        {trash.length > 0 && <span className="text-xs text-gray-400 ml-auto">{trash.length}</span>}
      </button>
    </div>
  );
}

// ==================== MAIN PAGE ====================

export default function MessagesPage() {
  const { t } = useTranslation();
  const { canView, canCreate, canDelete } = usePermissions();
  const { user } = useAuth();
  const toast = useToast();
  const [inbox, setInbox] = useState([]);
  const [outbox, setOutbox] = useState([]);
  const [trash, setTrash] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMsg, setSelectedMsg] = useState(null);
  const [messageDetail, setMessageDetail] = useState(null);
  const [composing, setComposing] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [forwardMsg, setForwardMsg] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [activeFilter, setActiveFilter] = useState('inbox_all');
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [treePct, setTreePct] = useState(20);
  const [listPct, setListPct] = useState(40);

  const loadInbox = useCallback(async () => {
    try { const r = await api.get('/messages/inbox'); setInbox(r.data.messages); } catch {}
  }, []);
  const loadOutbox = useCallback(async () => {
    try { const r = await api.get('/messages/outbox'); setOutbox(r.data.messages); } catch {}
  }, []);
  const loadTrash = useCallback(async () => {
    try { const r = await api.get('/messages/trash'); setTrash(r.data.messages); } catch {}
  }, []);
  const initialLoadDone = React.useRef(false);
  const loadAll = useCallback(async () => {
    if (!initialLoadDone.current) setLoading(true);
    await Promise.all([loadInbox(), loadOutbox(), loadTrash()]);
    setLoading(false);
    initialLoadDone.current = true;
  }, [loadInbox, loadOutbox, loadTrash]);

  useEffect(() => { loadAll(); }, [loadAll]);
  const drafts = outbox.filter(m => m.is_draft);
  const sent = outbox.filter(m => !m.is_draft);
  // Set initial filter only once
  const initialFilterSet = React.useRef(false);
  useEffect(() => {
    if (!loading && !initialFilterSet.current && inbox.length >= 0) {
      setFilteredMessages(inbox);
      initialFilterSet.current = true;
    }
  }, [loading, inbox]);

  const handleFilterChange = async (filterKey, folder, tenantId, messages) => {
    setActiveFilter(filterKey);
    setFilteredMessages(messages);
    setSearchQuery('');
    // Auto-select newest message
    const sorted = [...messages].sort((a, b) => new Date(b.sent_at || b.created_at) - new Date(a.sent_at || a.created_at));
    if (sorted.length > 0) {
      const newest = sorted[0];
      setSelectedMsg(newest);
      try {
        const r = await api.get(`/messages/${newest.id}`);
        setMessageDetail(r.data);
        loadAll();
      } catch {}
    } else {
      setSelectedMsg(null);
      setMessageDetail(null);
    }
  };

  const toggleRead = async (msg, currentlyRead) => {
    try {
      if (currentlyRead) {
        await api.put(`/messages/${msg.id}/unread`);
        const update = m => m.id === msg.id ? { ...m, read_at: null } : m;
        setInbox(prev => prev.map(update));
        setFilteredMessages(prev => prev.map(update));
      } else {
        await api.get(`/messages/${msg.id}`); // marks as read
        const update = m => m.id === msg.id ? { ...m, read_at: new Date().toISOString() } : m;
        setInbox(prev => prev.map(update));
        setFilteredMessages(prev => prev.map(update));
      }
    } catch {}
  };

  const selectMessage = async (msg) => {
    setSelectedMsg(msg);
    try {
      const r = await api.get(`/messages/${msg.id}`);
      setMessageDetail(r.data);
      // Mark as read locally without reloading everything
      if (!msg.read_at) {
        setInbox(prev => prev.map(m => m.id === msg.id ? { ...m, read_at: new Date().toISOString() } : m));
        setFilteredMessages(prev => prev.map(m => m.id === msg.id ? { ...m, read_at: new Date().toISOString() } : m));
      }
    } catch { toast.error(t('common.error')); }
  };

  const handleSend = async (data) => {
    try {
      await api.post('/messages', { ...data, is_draft: false });
      toast.success(t('messages.messageSent'));
      setComposing(false); setReplyTo(null); setForwardMsg(null); loadAll();
    } catch (err) { toast.error(err.response?.data?.error || t('common.error')); }
  };
  const handleSaveDraft = async (data) => {
    try {
      await api.post('/messages', { ...data, is_draft: true });
      toast.success(t('messages.draftSaved'));
      setComposing(false); loadAll();
    } catch (err) { toast.error(err.response?.data?.error || t('common.error')); }
  };
  const handleDelete = async () => {
    try {
      await api.delete(`/messages/${deleteTarget.id}`);
      toast.success(t('common.deleted')); setDeleteTarget(null);
      if (selectedMsg?.id === deleteTarget.id) { setSelectedMsg(null); setMessageDetail(null); }
      loadAll();
    } catch { toast.error(t('common.error')); }
  };

  if (!canView('messages')) {
    return <div className="p-8 text-center text-gray-400"><Mail size={48} className="mx-auto mb-4 opacity-50" /><p>{t('common.noAccess')}</p></div>;
  }

  const searchedMessages = searchQuery
    ? filteredMessages.filter(m =>
        m.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.sender_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.body?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : filteredMessages;
  const sortedFiltered = [...searchedMessages].sort((a, b) => new Date(b.sent_at || b.created_at) - new Date(a.sent_at || a.created_at));
  const msg = messageDetail?.message;
  const recipients = messageDetail?.recipients || [];

  // Horizontal splitter drag for tree width
  const treeRef = React.useRef(null);
  const containerRef = React.useRef(null);
  const handleTreeDrag = (e) => {
    e.preventDefault();
    const move = (ev) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      setTreePct(Math.min(40, Math.max(12, ((ev.clientX - rect.left) / rect.width) * 100)));
    };
    const up = () => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up); document.body.style.cursor = ''; document.body.style.userSelect = ''; };
    document.body.style.cursor = 'col-resize'; document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', move); document.addEventListener('mouseup', up);
  };

  // Vertical splitter drag for list/detail split
  const rightRef = React.useRef(null);
  const handleListDrag = (e) => {
    e.preventDefault();
    const move = (ev) => {
      if (!rightRef.current) return;
      const rect = rightRef.current.getBoundingClientRect();
      setListPct(Math.min(80, Math.max(15, ((ev.clientY - rect.top) / rect.height) * 100)));
    };
    const up = () => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up); document.body.style.cursor = ''; document.body.style.userSelect = ''; };
    document.body.style.cursor = 'row-resize'; document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', move); document.addEventListener('mouseup', up);
  };

  if (composing) {
    return (
      <div className="p-6">
        <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-4"><Send size={20} /> {t('messages.newMessage')}</h2>
        <ComposeForm key={`compose_${replyTo?.id || 'new'}_${forwardMsg?.id || ''}`}
          onSend={handleSend} onSaveDraft={handleSaveDraft} onCancel={() => { setComposing(false); setReplyTo(null); setForwardMsg(null); }}
          replyTo={replyTo} forwardMsg={forwardMsg} />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col p-4 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-3 flex-shrink-0">
        <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
          <Mail size={24} className="text-blue-600" /> {t('messages.title')}
        </h1>
        {canCreate('messages') && (
          <button onClick={() => setComposing(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm">
            <Plus size={16} /> {t('messages.newMessage')}
          </button>
        )}
      </div>

      {/* Main layout: tree | (list / detail) */}
      <div ref={containerRef} className="flex-1 flex min-h-0">
        {/* Left: Tree */}
        <div style={{ width: `${treePct}%` }} className="flex-shrink-0 bg-white border border-gray-200 rounded-lg overflow-auto p-2">
          {loading ? <div className="p-4 text-gray-400 text-sm">{t('common.loading')}</div> : (
            <MessageTree inbox={inbox} outbox={sent} drafts={drafts} trash={trash} activeFilter={activeFilter} onFilterChange={handleFilterChange} userTenantId={user.tenant_id} />
          )}
        </div>

        {/* Vertical splitter (tree | right) */}
        <div onMouseDown={handleTreeDrag} className="w-2 flex-shrink-0 cursor-col-resize group flex items-center justify-center hover:bg-blue-100 transition-colors">
          <div className="w-0.5 h-8 bg-gray-300 group-hover:bg-blue-500 rounded-full transition-colors" />
        </div>

        {/* Right: List + Detail */}
        <div ref={rightRef} className="flex-1 flex flex-col min-w-0">
          {/* Search bar */}
          <div className="flex-shrink-0 mb-1">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                placeholder={t('common.search') + '...'} className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm bg-white" />
            </div>
          </div>

          {/* Top: Message table */}
          <div style={{ height: `${listPct}%` }} className="bg-white border border-gray-200 rounded-lg overflow-hidden flex-shrink-0">
            <div className="overflow-auto h-full">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-gray-500 w-8"></th>
                    <th className="text-left px-3 py-2 font-medium text-gray-500">{t('messages.subjectLabel').replace(' *', '')}</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-500 w-48">{t('messages.fromLabel')}</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-500 w-40">{t('messages.dateLabel')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedFiltered.length === 0 && (
                    <tr><td colSpan={4} className="text-center py-8 text-gray-400">{t('messages.emptyInbox')}</td></tr>
                  )}
                  {sortedFiltered.map(m => {
                    const isRead = !!m.read_at || activeFilter.startsWith('outbox');
                    const isSelected = selectedMsg?.id === m.id;
                    return (
                      <tr key={m.id} onClick={() => selectMessage(m)}
                        className={`cursor-pointer border-b border-gray-50 hover:bg-blue-50 transition ${isSelected ? 'bg-blue-100' : ''} ${!isRead ? 'font-bold' : ''}`}>
                        <td className="px-3 py-2">
                          <button onClick={e => { e.stopPropagation(); toggleRead(m, isRead); }}
                            className="w-4 h-4 flex items-center justify-center rounded-full hover:bg-gray-200"
                            title={isRead ? t('messages.markUnread') : t('messages.markRead')}>
                            <span className={`w-2 h-2 rounded-full inline-block ${!isRead ? 'bg-blue-500' : 'bg-gray-300'}`} />
                          </button>
                        </td>
                        <td className="px-3 py-2 truncate max-w-0">
                          <span className={!isRead ? 'text-gray-900' : 'text-gray-600'}>{m.subject}</span>
                        </td>
                        <td className="px-3 py-2 text-gray-500 truncate">
                          {activeFilter.startsWith('outbox') ? `→ ${m.recipients?.map(r => r.name).join(', ') || ''}` : m.sender_name}
                        </td>
                        <td className="px-3 py-2 text-gray-400 text-xs whitespace-nowrap">
                          {new Date(m.sent_at || m.created_at).toLocaleString('pl-PL')}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Horizontal splitter (list / detail) */}
          <div onMouseDown={handleListDrag} className="h-2 flex-shrink-0 cursor-row-resize group flex items-center justify-center hover:bg-blue-100 transition-colors">
            <div className="h-0.5 w-8 bg-gray-300 group-hover:bg-blue-500 rounded-full transition-colors" />
          </div>

          {/* Bottom: Message detail */}
          <div className="flex-1 bg-white border border-gray-200 rounded-lg overflow-auto min-h-0">
            {msg ? (
              <div className="p-4">
                <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-100">
                  <div>
                    <h2 className="text-lg font-bold text-gray-800">{msg.subject}</h2>
                    <div className="text-sm text-gray-500 mt-1">
                      {t('messages.fromLabel')}: <strong className="text-gray-700">{msg.sender_name}</strong> &lt;{msg.sender_email}&gt;
                      <span className="ml-3 text-xs text-gray-400">{new Date(msg.sent_at || msg.created_at).toLocaleString('pl-PL')}</span>
                    </div>
                    {recipients.length > 0 && (
                      <div className="text-xs text-gray-400 mt-0.5">
                        {t('messages.toLabel').replace(' *', '')}: {recipients.map(r => r.recipient_name).join(', ')}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => { setReplyTo(msg); setComposing(true); }}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded">
                      <Reply size={14} /> {t('messages.reply')}
                    </button>
                    <button onClick={() => { setForwardMsg(msg); setComposing(true); }}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded">
                      <Forward size={14} /> {t('messages.forward')}
                    </button>
                    <button onClick={() => window.print()}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded">
                      <Printer size={14} />
                    </button>
                    {canDelete('messages') && (
                      <button onClick={() => setDeleteTarget(msg)}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-sm text-red-500 hover:bg-red-50 rounded">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{msg.body}</div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                {t('messages.selectMessage')}
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog isOpen={!!deleteTarget} type="danger"
        title={t('messages.deleteMessage')} message={t('messages.deleteMessageConfirm', { subject: deleteTarget?.subject })}
        onConfirm={handleDelete} onCancel={() => setDeleteTarget(null)} />
    </div>
  );
}
